import { apiCall } from './apiClient';
import { WORKFLOW_CATEGORY_OPTIONS, workflowCategoriesForTypes } from '../constants/workflowCategories';
import type { ProjectPortfolio, ProjectDetail, ProjectTimelinePayload, ProjectFilters } from '../types/projectTracker';
export type TrackerView = 'List' | 'Grid' | 'Timeline';
interface TrackerPreferences {
    filters: ProjectFilters;
    view: TrackerView;
    range: '30' | '90' | 'fit';
    zoom: number;
}
const preferences = new Map<string, TrackerPreferences>();
const preferenceKey = (scopeKey: string) => `project-tracker-preferences:v1:${encodeURIComponent(scopeKey)}`;
const preferenceSorts = ['last_activity', 'updated_at', 'title', 'workflow_type', 'phase_duration', 'created_at', 'value'];
function validPreferences(value: any): TrackerPreferences | undefined {
    if (!value || typeof value !== 'object' || !value.filters || typeof value.filters !== 'object')
        return undefined;
    const saved = value.filters;
    const selections = (values: unknown, maxLength: number) => Array.from(new Set((Array.isArray(values) ? values : typeof values === 'string' ? [values] : []).filter((item): item is string => typeof item === 'string' && item.length > 0 && item.length <= maxLength))).slice(0, 100);
    const validSort = preferenceSorts.includes(saved.sort);
    const categories = saved.workflow_category === undefined
        ? workflowCategoriesForTypes(selections(saved.workflow_type, 100))
        : selections(saved.workflow_category, 100).filter(value => WORKFLOW_CATEGORY_OPTIONS.some(option => option.value === value));
    return {
        filters: {
            search: typeof saved.search === 'string' ? saved.search.slice(0, 200) : '',
            workflow_category: categories, owner: selections(saved.owner, 320),
            has_price_estimate: saved.has_price_estimate === true,
            status: 'active', sort: validSort ? saved.sort : 'last_activity',
            direction: validSort && ['asc', 'desc'].includes(saved.direction) ? saved.direction : 'desc',
            page: validSort && Number.isInteger(saved.page) && saved.page > 0 && saved.page <= 100000 ? saved.page : 1,
            page_size: Number.isInteger(saved.page_size) && saved.page_size >= 1 && saved.page_size <= 100 ? saved.page_size : 25,
        },
        view: 'Timeline',
        range: ['30', '90', 'fit'].includes(value.range) ? value.range : '30',
        zoom: Number.isInteger(value.zoom) && value.zoom >= 1 && value.zoom <= 4 ? value.zoom : 1,
    };
}
export function getProjectTrackerPreferences(scopeKey: string): TrackerPreferences | undefined {
    if (!scopeKey) return undefined;
    const memory = preferences.get(scopeKey);
    if (memory) return validPreferences(memory);
    try {
        const saved = JSON.parse(localStorage.getItem(preferenceKey(scopeKey)) || 'null');
        return saved?.version === 1 ? validPreferences(saved) : undefined;
    }
    catch { return undefined; }
}
export function setProjectTrackerPreferences(scopeKey: string, filters: ProjectFilters, view: TrackerView, timeline?: Pick<TrackerPreferences, 'range' | 'zoom'>): void {
    if (!scopeKey) return;
    const saved = validPreferences({ filters, view, ...timeline })!;
    preferences.set(scopeKey, saved);
    try { localStorage.setItem(preferenceKey(scopeKey), JSON.stringify({ version: 1, ...saved })); }
    catch { /* Storage may be disabled; in-app navigation still retains preferences. */ }
}
let generation = 0;
const pending = new Set<AbortController>();
const listeners = new Set<(failure?: Error) => void>();
export function clearProjectTrackerProtectedState(failure?: Error): void {
    generation += 1;
    // Preferences contain controls only. Protected responses and pending reads
    // are always cleared; a later authorized visit can restore its own controls.
    preferences.clear();
    pending.forEach(controller => controller.abort());
    pending.clear();
    // Notify failures with the reset itself: the originating read is fenced
    // out by listeners, so its later rejection cannot display the error.
    listeners.forEach(listener => listener(failure));
}
export function onProjectTrackerProtectedReset(listener: (failure?: Error) => void): () => void {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
export const isTrackerCancellation = (error: unknown) => error instanceof Error && error.name === 'AbortError';
const cancelled = () => new DOMException('Request cancelled', 'AbortError');
async function read<T>(path: string, signal?: AbortSignal): Promise<T> {
    const requestGeneration = generation;
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted)
        controller.abort();
    pending.add(controller);
    try {
        const response = await apiCall<T>(path, 'GET', undefined, { signal: controller.signal, retries: 0, timeoutMs: 60000 });
        if (requestGeneration !== generation || controller.signal.aborted)
            throw cancelled();
        if (!response.success || !response.data) {
            const error = Object.assign(new Error(response.error || 'Could not load project information.'), { error_code: response.error_code });
            if (['forbidden', 'unauthorized', 'project_tracker_scope_changed', 'project_tracker_forbidden', 'project_tracker_unit_unavailable', 'project_tracker_scope_unavailable', 'project_tracker_unavailable', 'auth_required'].includes(response.error_code || ''))
                clearProjectTrackerProtectedState(error);
            throw error;
        }
        return response.data;
    }
    finally {
        pending.delete(controller);
        signal?.removeEventListener('abort', abort);
    }
}
export function getProjectPortfolio(filters: ProjectFilters, signal?: AbortSignal) {
    const query = new URLSearchParams({ include_timing: 'true' });
    Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value))
            value.forEach(selected => query.append(key, selected));
        else if (value !== '')
            query.set(key, String(value));
    });
    return read<ProjectPortfolio>(`/api/project-tracker/projects?${query}`, signal);
}
export function getProjectDetail(flowId: string, signal?: AbortSignal) {
    return read<{
        scope_revision: string;
        collected_at: string;
        project: Omit<ProjectDetail, 'scope_revision' | 'collected_at' | 'timing'>;
        timing: ProjectDetail['timing'];
    }>(`/api/project-tracker/projects/${encodeURIComponent(flowId)}`, signal).then(data => ({ ...data.project, scope_revision: data.scope_revision, collected_at: data.collected_at, timing: data.timing }));
}
export function getProjectTimeline(flowIds: string[], signal?: AbortSignal) {
    const query = new URLSearchParams({ flow_ids: flowIds.join(',') });
    return read<ProjectTimelinePayload>(`/api/project-tracker/projects/timeline?${query}`, signal);
}
